import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEvent,
  FocusEventHandler,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useFormContext,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { TextFieldType, ValidationOptions } from '../../../types/form';
import { transformValidationOptions } from '../../../helpers/shared/transformValidationOptions';
import { applyTextValidationOptions } from './applyTextValidationOptions';

export type TextFieldValidationOptions<TFieldValues> =
  ValidationOptions<TFieldValues> & {
    postcode?: boolean;
    inFrontAllowedChars?: boolean;
    format?: string;
  };

export interface UseTextFieldControllerProps<TFieldValues>
  extends Omit<UseControllerProps<TFieldValues>, 'rules'> {
  rules?: TextFieldValidationOptions<TFieldValues>;
  type?: TextFieldType;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

export function useTextFieldController<TFieldValues>(
  props: UseTextFieldControllerProps<TFieldValues>,
) {
  const {
    type = 'text',
    name,
    onBlur,
    onChange,
    rules,
    ...textFieldControllerProps
  } = props;

  const { t } = useTranslation();
  const { control, getValues } = useFormContext<TFieldValues>();

  const validationOptions = transformValidationOptions<TFieldValues>(
    t,
    rules,
    getValues,
  );
  const textFieldRules = applyTextValidationOptions<TFieldValues>(
    t,
    type,
    validationOptions,
  );
  const required = typeof textFieldRules.required !== 'undefined';

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: textFieldRules,
    ...textFieldControllerProps,
  });
  const { field } = controllerProps;

  const trimValue = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (type === 'number') return;

      const value = event.target.value;
      const trimmedValue = value.trim();

      if (value !== trimmedValue) {
        field.onChange(trimmedValue);
      }
    },
    [field, type],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, valueAsNumber } = event.target;

      if (type === 'number') {
        const newValue = valueAsNumber || value;
        field.onChange(newValue);
      } else {
        field.onChange(event);
      }

      if (onChange) {
        onChange(event);
      }
    },
    [field, onChange, type],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      trimValue(event);
      field.onBlur();

      if (onBlur) {
        onBlur(event);
      }
    },
    [field, onBlur, trimValue],
  );

  return {
    required,
    onChangeField: handleChange,
    onBlurField: handleBlur,
    ...controllerProps,
  };
}
